<template>
  <div class="container">
    <div class="status">
      <div class="circle-2nd" />
      <div class="circle-3nd">
        <div class="icon-container">
          <img v-if="currentStep === stepEnum['CONNECTING']" src="@/assets/img/voice/call.svg" alt="call" />
          <img v-else-if="currentStep === stepEnum['CONNECTED']" src="@/assets/img/voice/avatar.svg" alt="avatar" />
        </div>
      </div>
      <div v-if="currentStep === stepEnum['CONNECTING']" class="connecting">Connecting...</div>
      <div v-else-if="currentStep === stepEnum['CONNECTED']" class="time">
        {{ formattedTime }}
      </div>
    </div>
    <div class="actions">
      <div
        v-track-click="{
          name: BTN_EVTS.VOICE_CHAT_END,
          selection: voiceChatId,
        }"
        class="hangup"
        @click="handleHangUp"
      >
        <div class="icon-container">
          <HangupIcon />
        </div>
      </div>
      <div v-if="showMinimize" class="hangup" @click="minimize">
        <div class="icon-container">
          <MinimizeIcon />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import HangupIcon from '@/components/icons/HangupIcon.vue';
import MinimizeIcon from '@/components/icons/MinimizeIcon.vue';
import { Prop, Component, Vue } from 'vue-property-decorator';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import trackClick, { BTN_EVTS } from '@/directives/trackClick';
import { checkVersionSupported } from '@/common/bot/flow-frameworks/VersionCheck.static';

enum stepEnum {
  CONNECTING,
  CONNECTED,
}

@Component({
  components: { HangupIcon, MinimizeIcon },
  mixins: [LogPageEventMixin],
  directives: {
    trackClick,
  },
})
export default class Voice extends Vue {
  @Prop(Number) readonly voiceChatId!: number | null;

  readonly BTN_EVTS = BTN_EVTS;

  get [PAGE_TRACKING]() {
    return {
      name: PAGE_EVENTS.VOICE_CHAT,
      selection: this.voiceChatId,
    };
  }

  get showMinimize() {
    return checkVersionSupported('voiceMinimize', false);
  }

  get formattedTime() {
    return this.formatTime(this.time);
  }

  formatTime(distance: number) {
    const minute = 60;
    const hour = minute * 60;
    const day = hour * 24;
    const hours = Math.floor((distance % day) / hour);
    const mins = Math.floor((distance % hour) / minute);
    const secs = Math.floor(distance % minute);
    const paddingDigit = (val: number) => (val > 9 ? val : `0${val}`);
    return `${paddingDigit(hours)}:${paddingDigit(mins)}:${paddingDigit(secs)}`;
  }

  time = 0;
  stepEnum = stepEnum;
  currentStep = stepEnum['CONNECTING'];
  timerId = 0;

  mounted() {
    document.title = 'Voice Call';
    this.$emit('call', this);
  }
  beforeDestroy() {
    clearTimeout(this.timerId);
    this.$emit('destroy');
  }
  handleConnected() {
    this.currentStep = stepEnum['CONNECTED'];
    this.timerId = window.setInterval(() => {
      this.time += 1;
    }, 1000);
  }
  handleHangUp() {
    this.$emit('close');
  }
  minimize() {
    this.$emit('minimize');
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/base.scss';
.container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  background-color: $voice-call-background;
  color: $voice-call-text-color;
  .status {
    background-color: $voice-call-ripple-2;
    width: 86.7vw;
    height: 86.7vw;
    border-radius: 100%;
    position: relative;
    margin-top: 4%;
    display: flex;
    justify-content: center;
    align-items: center;
    .circle-2nd {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: $voice-call-ripple-1;
      width: 53.89vw;
      height: 53.89vw;
      border-radius: 100%;
    }
    .circle-3nd {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: $voice-call-ripple-0;
      width: 31.67vw;
      height: 31.67vw;
      border-radius: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      .icon-container {
        width: 42%;
        height: 42%;
      }
    }
    .connecting {
      position: absolute;
      bottom: 60px;
      font-size: 16px;
    }
    .time {
      position: absolute;
      bottom: 42px;
      font-size: 32px;
    }
  }
  .actions {
    width: 100%;
    display: flex;
    justify-content: center;
    gap: 32px;
    margin-bottom: 9.5%;
  }
  .hangup {
    width: 19.4vw;
    height: 19.4vw;
    box-shadow: $voice-action-btn-shadow;
    border-radius: 100%;
    display: flex;
    justify-content: center;
    align-items: center;

    cursor: pointer;
    background-color: $voice-action-btn-background;
    .icon-container {
      width: 46%;
      height: 46%;
      color: $voice-action-btn-icon-color;
    }
  }
}
</style>
